import ApiServer from "../../ApiServer.ts";
import ISharableByMember from "./ISharableByMember.ts";
import * as i18n from "../../i18n.ts";
import * as FwDatas from "../../FwDatas.ts";
import * as Sorting from "../../lib/Sorting.ts";
import * as Locale from "../../util/Locale.ts";
import * as amplify from "../../util/amplify.ts";
import {SyncCache,ListCache, MetaCache4, UndoableCache} from "../../lib/SyncCache.ts";
import {recipeSyncCache} from "../../mealplanner/mealplannerApi.ts";
import IHasRecurrencyHelper, {type IHasRecurrency, type DateRange, type IHasRecurrencyOccurrence} from "../../lib/IHasRecurrencyHelper.ts";
import * as deltatime from "../../lib/deltatime.ts";
import {formatQuantityUnit, rememberUnit} from "../../lib/unitFormat.ts";
import * as Html from "../../util/Html.ts";
import Icons, {SvgFile} from "../../svg/Icons.ts";
import FizConfig from "../../FizConfig.ts";
import {
  ITaskList,
  TaskBean,
  TaskCategoryBean,
  type IMedia,
  type RecipeBean,
  type TaskListTypeEnum,
  type TaskSortingEnum
} from "../fizapilib.auto.ts";

import TPL_TASKLIST_EMPTY_TODO from "../../list/tasklist-empty-todo.mustache";
import TPL_TASKLIST_EMPTY_SHOPPING from "../../list/tasklist-empty-shopping.mustache";
import TPL_TASKLIST_EMPTY_OTHER from "../../list/tasklist-empty-other.mustache";
import TPL_TASKLIST_EMPTY_CLEARED from "../../list/tasklist-empty-cleared.mustache";

import {mustache,SafeString} from "../../handlebars_family.ts";
import { isFeatureEnabled } from "../../lib/launchdarkly/launchdarkly.ts";

type HandlebarsSafeString = InstanceType<typeof SafeString>;
type TaskSorter = (a: TaskBean, b: TaskBean) => number;

/** A single occurrence of a (possibly recurrent) task, prototype-linked to its TaskBean */
export type TaskOccurrence = TaskBean & IHasRecurrencyOccurrence;

/** One group of the "grouped by category" view of a task list */
export interface TaskCategoryGroup {
  category: TaskCategoryBean;
  items: TaskBean[];
}

/** Description of the current sorting, displayed as a cartridge on top of a list */
export interface TaskSortCartridge {
  name: string;
  reverseonclick: string;
  up: boolean;
}

/** Which sortings make sense for a given list */
export interface TaskSortAvailability {
  alphabetical: boolean;
  creationdate: boolean;
  custom: boolean;
  byrecipe: boolean;
  duedate: boolean;
}

/** Last action performed on a task, displayed in the task details */
export interface TaskRefAction {
  text: string;
  author: any;
  date: Date;
}

// Augmentation of the generated beans with the properties and methods added dynamically below
declare module "../fizapilib.auto.ts" {
  interface FizRightBean {
    /** Computed client side by the owning bean onDecode (task list, budget, calendar, ...) */
    canShare?: boolean;
  }
  interface AssigneeBean {
    /** TaskBean.onDecode replaces the raw assignees by the matching family profiles */
    name?: string;
    /** idem, the color of the profile */
    color?: string;
  }
  interface ITaskList {
    // Methods added by ISharableByMemberProto
    isShared(): boolean;
    sharedMembers(): any[];
    sharedCartridge(onclick: string): string;

    // Properties computed in onDecode
    sharingType?: 'all' | 'custom' | 'none';
    emojiValid?: string;
    system?: boolean;
    iconsimple?: SvgFile;
    wallKey?: string;
    members?: any[];
    firstMedia?: IMedia;
    /** transient UI state, kept across decodes */
    closed?: boolean;

    // Tasks of this list, dispatched by dispatchTasks()
    all?: TaskBean[];
    ongoing?: TaskBean[];
    complete?: TaskBean[];
    overDue?: boolean;

    // Methods added via Object.assign
    dispatchTasks(tasks: TaskBean[]): void;
    tplEmpty(): HandlebarsSafeString | undefined;
    taskByCategory(): TaskCategoryGroup[];
    i18ncomplete(): string;
    sortCartridge(): TaskSortCartridge | undefined;
    mightsort(): TaskSortAvailability | undefined;
    getCategories(localeFilter?: boolean): TaskCategoryBean[];
    sort(): void;
  }
  /** a task is recurrent through its dueDate, mapped on the IHasRecurrency startDate/endDate */
  interface TaskBean extends IHasRecurrency {
    // Properties computed in onDecode / listInterval
    computedComplete?: boolean;
    computedDueDate?: Date;
    overDue?: boolean;
    isodueDate?: string;
    displayAssignee?: boolean;
    /** the only occurrence of a recurrent task which is displayed in the list and markable */
    maintask?: boolean;
    uncheckable?: boolean;
    /** title used by the calendar views */
    title?: string;
    rights?: {canDelete: boolean, canUpdate: boolean};
    /** transient flag set by the wall to animate the checkbox */
    justChange?: boolean;
    /** color of the owning list, copied by ITaskList.dispatchTasks() */
    color?: string;
    /** the owning list, set by ITaskList.dispatchTasks() */
    list?: ITaskList;

    // a non recurrent task is its own single occurrence
    start?: Date;
    end?: Date;

    // Methods added via Object.assign
    listInterval(range: DateRange, extra?: number): TaskOccurrence[];
    refActionData(): TaskRefAction | undefined;
    category(): TaskCategoryBean | undefined;
    quantityActive(): boolean;
    quantityLabel(): string;
    subLine(): string;
    refObject(): RecipeBean | undefined;
  }
}

function completeSorter(a: TaskBean, b: TaskBean): number {
  if(a.complete===b.complete){
    return 0;
  }
  return a.complete?1:-1;
}
const TASK_SORTER: Partial<Record<TaskSortingEnum | 'modifDate', TaskSorter>> = {

    alphabetical:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return a.text.localeCompare(b.text)
    }),
    alphabeticalReverse:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return b.text.localeCompare(a.text)
    }),
    creationdate:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return b.creationDate.getTime() - a.creationDate.getTime();
    }),
    creationdateReverse:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return a.creationDate.getTime() - b.creationDate.getTime();
    }),
    duedate:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return (b.dueDate?.getTime()||0) - (a.dueDate?.getTime()||0);
    }),
    duedateReverse:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return (a.dueDate?.getTime()||0) - (b.dueDate?.getTime()||0);
    }),
    modifDate:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return (b.modifDate?.getTime()||0) - (a.modifDate?.getTime()||0);
    }),
    byrecipe:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      let
        aRef = a.refObject(),
        bRef = b.refObject();
      //if no refAt all or same ref
      if(aRef===bRef){
        return a.text.localeCompare(b.text);
      }
      if(aRef===undefined){
        return 1;
      }
      if(bRef===undefined){
        return -1;
      }
      //if same ref name fallback to task name compare
      if(aRef.name===bRef.name){
        return a.text.localeCompare(b.text);
      }
      return aRef.name.localeCompare(bRef.name);
    }),
    byrecipeReverse:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return TASK_SORTER.byrecipe(b,a);
    }),

    custom:Sorting.composedSorter<TaskBean>(completeSorter,function(a,b){
      return b.sortingIndex - a.sortingIndex;
    })
}

var emojiRegexp: RegExp;
try{
  emojiRegexp = new RegExp('^\\p{RGI_Emoji}$','v');
}catch(e){
  console.log(e)
}

function decodeHasEmoji(obj: ITaskList): void {
  //the syntax of regexp with / make webpack explode
  if(emojiRegexp===undefined || emojiRegexp.test(obj.emoji)){
    obj.emojiValid = obj.emoji;
  }
}

/**
 * sortingIndexByTaskList is decoded by decodeMap() which builds a plain object, so the generated
 * `Map<string, number>` type does not match the runtime value.
 */
function categorySortingIndex(category: TaskCategoryBean, taskListId: string): number {
  const indexByTaskList = category.sortingIndexByTaskList as unknown as Record<string, number>;
  return indexByTaskList[taskListId] || category.initialSortingIndex;
}

/** The generated TaskCategoryBeanJSON type is not exported, hence the untyped json */
function uncategorizedCategory(): TaskCategoryBean {
  return new TaskCategoryBean({
    emoji:'🏷️',
    metaId:"",
    name:i18n.get('catsystem.uncategorized'),
    sortingIndexByTaskList:{},
    initialSortingIndex:1000000000000
  } as any);
}

Object.assign(ITaskList.prototype,ISharableByMember)
Object.assign(ITaskList.prototype,{
  onDecode:function(this: ITaskList, json){
    //first of get transient data like is the list opened
    this.rights.canShare = this.accountId === FwDatas.getUser().accountId;
    this.sharingType = this.sharedToAll?'all':(this.sharedMemberIds.length>1?'custom':'none')
    decodeHasEmoji(this);

    if(this.systemId){
      if(this.name==='SYS-CAT-TODOS'){
        this.name = i18n.get('catsystem.SYS-CAT-TODOS');
      }else if(this.name==='SYS-CAT-SHOPPINGLIST'){
        this.name = i18n.get('catsystem.SYS-CAT-SHOPPINGLIST');
      }
      this.system = true;
      this.rights.canUpdate = true; //20230105 now we can update a part of the List
    }
    if(this.taskListType==='TODOS'){
      this.iconsimple = Icons.list.ic_list_item_type_todo;
    }else if(this.taskListType==='SHOPPING_LIST'){
      this.iconsimple = Icons.list.ic_list_item_type_groceries;
    }else{
      this.iconsimple = Icons.ic_list;
    }
    if(this.color===undefined) {
      this.color = FizConfig.defaultColor;
    }
    this.wallKey = this.lastAction==="UPDATED"?"notifications.TASKLISTUPDATED":"notifications.TASKLISTCREATED";
    this.members = this.sharedMembers();
    this.firstMedia = this.medias&&this.medias.length?this.medias[0]:null;

    //TODO replace complete/all/ongoing by getter that dig the datas
    let
      oldList: ITaskList = FwDatas.get(this.metaId);
    if(oldList){
      this.closed = oldList.closed;
      this.complete = oldList.complete;
      this.all = oldList.all;
      this.ongoing = oldList.ongoing;
    }else{
      this.complete = [];
      this.all = [];
      this.ongoing = [];
    }
    FwDatas.cache(this);

  },
  dispatchTasks(this: ITaskList, tasks: TaskBean[]): void {
    this.complete = [];
    this.all = [];
    this.ongoing = [];
    tasks.forEach(task=>{
      if(task.taskListId===this.metaId){

        task.color = this.color;
        this.all.push(task);
        if(task.computedComplete){
          this.complete.push(task);
        }else{
          this.ongoing.push(task);
          this.overDue = this.overDue || task.overDue;
        }
        task.list=this;
      }
    });
    this.sort();
  },
  tplEmpty:function(this: ITaskList): HandlebarsSafeString | undefined {
    if(this.all.length===0){
      if(this.taskListType==='TODOS'){
        return new SafeString(mustache(TPL_TASKLIST_EMPTY_TODO,this));
      }else if(this.taskListType==='SHOPPING_LIST'){
        return new SafeString(mustache(TPL_TASKLIST_EMPTY_SHOPPING,this));
      }else if(this.taskListType==='OTHER'){
        return new SafeString(mustache(TPL_TASKLIST_EMPTY_OTHER,this));
      }
    }else if(this.ongoing.length===0 && this.complete.length && this.completedHidden){
      return new SafeString(mustache(TPL_TASKLIST_EMPTY_CLEARED,this));
    }
  },
  taskByCategory(this: ITaskList): TaskCategoryGroup[] {
    let
      result: TaskCategoryGroup[] = [{
          category:uncategorizedCategory(),
          items:[]
        }].concat(this.getCategories().map(cat=>{return {category:cat,items:[]}})),
      itemsByCatid: Record<string, TaskBean[]> = {};
    result.forEach(group=>itemsByCatid[group.category.metaId]=group.items)

    this.all.forEach(task=>{
      if(this.completedHidden && task.computedComplete) return;
      if(itemsByCatid[task.taskCategoryId]==undefined){
        itemsByCatid[""].push(task)
      }else{
        itemsByCatid[task.taskCategoryId].push(task);
      }
    })
    result = result.filter(catdesc=>catdesc.items.length>0)
    //force to sort again for the uncategorized items
    result.sort(Sorting.levelSorter<TaskCategoryGroup>(a=>{
      return a.category?categorySortingIndex(a.category,this.metaId):0;
    }));


    result.forEach(catitem=>catitem.items.sort(TASK_SORTER[this.taskSorting]));
    return result;
  },
  i18ncomplete : function(this: ITaskList): string {
    return this.complete.length===1?i18n.get('task_details.complete'):i18n.get('task_details.completeN',this.complete.length)
  },
  sortCartridge:function(this: ITaskList): TaskSortCartridge | undefined {
    if(this.taskSorting==='custom') return;
    if(!this.taskSorting) return;
    let
      isUp = this.taskSorting.indexOf('Reverse')===-1,
      reverse = ({
        "alphabetical":"alphabeticalReverse",
        "alphabeticalReverse":"alphabetical",
        "duedate":"duedateReverse",
        "duedateReverse":"duedate",
        "creationdate":"creationdateReverse",
        "creationdateReverse":"creationdate",
        "byrecipe":"byrecipeReverse",
        "byrecipeReverse":"byrecipe"
      } as Partial<Record<TaskSortingEnum, TaskSortingEnum>>)[this.taskSorting];

    return {
      name:  i18n.get('catsystem.sortedby.'+this.taskSorting),
      reverseonclick: 'tasklistsort|'+reverse,
      up:isUp
    }
  },
  mightsort:function(this: ITaskList): TaskSortAvailability | undefined {
    if(this.ongoing.length>1||this.complete.length>1){
      return {
          alphabetical:true,
          creationdate:true,
          custom:true,
          byrecipe:this.all.filter(task=>task.refObject()!==undefined).length>0,
          duedate:this.all.filter(task=>task.dueDate!==undefined).length>0,
      }
    }
  },
  getCategories(this: ITaskList, localeFilter = false): TaskCategoryBean[] {
    var
      locale = Locale.get().substring(0,2),
      metaId = this.metaId,
      filterCategories = ({
        TODOS:function(cat: TaskCategoryBean){return cat.taskListType==='TODOS'},
        SHOPPING_LIST:function(cat: TaskCategoryBean){return cat.taskListType==='SHOPPING_LIST'},
        OTHER:function(cat: TaskCategoryBean){return cat.taskListType==='OTHER' && cat.filterTasklistId===metaId}
      } as Partial<Record<TaskListTypeEnum, (cat: TaskCategoryBean)=>boolean>>)[this.taskListType],
      categories = TaskCategoryCache.datas
        .filter(filterCategories)
        .filter(cat=>cat.hiddenByTaskList.indexOf(metaId)===-1);

    if(localeFilter){
      categories = categories.filter(cat=>(cat.locale===locale || !cat.locale));
    }

    categories.sort((a,b)=>{
      return categorySortingIndex(a,metaId) - categorySortingIndex(b,metaId);
    })
    //console.log(categories.map(cat=>cat.name+'\t'+categorySortingIndex(cat,metaId)).join('\r\n'))
    return categories
  },
  sort:function(this: ITaskList): void {
    this.ongoing.sort(TASK_SORTER[this.taskSorting])
    this.complete.sort(TASK_SORTER[this.taskSorting])
  }
})


Object.assign(TaskBean.prototype,{
  listInterval(this: TaskBean, range: DateRange, extra = 0): TaskOccurrence[] {
    //we change the range because 0 needed to compute well if it is completed
    let instanceUntil = IHasRecurrencyHelper.listInterval(this, range,extra+2) as TaskOccurrence[];
    this.rights = this.editable?{canDelete:true,canUpdate:true}:{canDelete:false,canUpdate:false}
    //first of all we set the attribute well
    for(let i=0;i<instanceUntil.length;i++){
      let
        loopTask = instanceUntil[i];
      loopTask.computedDueDate = loopTask.start;
    }
    if(this.recurrencyDescriptor.rrule){
      //then we compute the "complete" per instance
      for(let i=0;i<instanceUntil.length-1;i++){
        let
          loopTask = instanceUntil[i],
          next = instanceUntil[i+1];
        if(!this.completedDate){
          loopTask.computedComplete = false;
          next.computedComplete = false;
        }else if(this.completedDate>=loopTask.computedDueDate){
          loopTask.computedComplete = true;
          next.computedComplete = true;
        }else{
          if(i===0)loopTask.computedComplete = true;
          next.computedComplete = false;
        }
      }
      //now we try to find the "main" task the one which will appears in the list and which is markable
      for(let i=0;i<instanceUntil.length;i++){
        let loopTask = instanceUntil[i];
        if(loopTask.start>new Date()){
          if(i>0 && !instanceUntil[i-1].computedComplete){
            instanceUntil[i-1].maintask = true;
          }else{
            loopTask.maintask = true;
          }
          break;
        }
      }
      for(let i=0;i<instanceUntil.length;i++){
        let loopTask = instanceUntil[i];
        if(!loopTask.maintask){
          loopTask.editable = false;
          loopTask.uncheckable = true;
        }
      }
    }


    instanceUntil.forEach(taskInstance=>{
      if(taskInstance.computedComplete){
        taskInstance.title = '☑ '; //title for calendar.js
      }else{
        taskInstance.title = '☐ '
      }
      if(taskInstance.recurrencyDescriptor.rrule){
        taskInstance.title += '↺ ';
      }
      taskInstance.title += taskInstance.text;

      taskInstance.overDue = !taskInstance.computedComplete && taskInstance.computedDueDate<new Date();
      FwDatas.cache(taskInstance)
    })

    return instanceUntil;
  },
  refActionData:function(this: TaskBean): TaskRefAction | undefined {
    if(this.lastAction==='MARK_COMPLETED'){
      return {text:i18n.get('refAction.completed'),author:FwDatas.getProfile(this.lastActionAuthor),date:this.lastActionDate};
    }else if(this.lastAction==='UPDATED'){
      return {text:i18n.get('refAction.updated'),author:FwDatas.getProfile(this.lastActionAuthor),date:this.lastActionDate};
    }
  },
  onDecode:function(this: TaskBean, json){
    //some data from server appear to not have text (but just an url as description)
    if(!this.text){
      this.text= '  '
    }
    for(var ii=0;ii<this.assignee?.length;ii++){
      this.assignee[ii] = FwDatas.getProfile(this.assignee[ii].accountId);
    }
    this.displayAssignee = !((this.assignee.length===1 && this.assignee[0].accountId===FwDatas.getUser().accountId));

    if(this.dueDate){
      this.start = this.end = this.endDate = this.startDate = this.dueDate;
      this.isodueDate = this.dueDate.toISOString();
      if(this.recurrencyDescriptor.rrule){
        let
          occurencies = this.listInterval({start:new Date(0),end:new Date()},1),
          mainOccurence = occurencies.filter(t=>t.maintask ||t.computedComplete===false)[0];

        if(mainOccurence){
          this.computedComplete = mainOccurence.computedComplete;
          this.computedDueDate = mainOccurence.computedDueDate;
          this.overDue = mainOccurence.overDue;
        }else{
          //FIXME understand it should never happen
          this.computedComplete = this.complete;
          this.computedDueDate = this.dueDate;
          this.overDue = !this.computedComplete && this.computedDueDate<new Date();
        }
      }else{
        this.computedComplete = this.complete;
        this.computedDueDate = this.dueDate;
        this.overDue = !this.computedComplete && this.computedDueDate<new Date();
      }


    }else{
      this.computedComplete = this.complete;
      this.isodueDate = "$empty";
    }
  },
  category : function(this: TaskBean): TaskCategoryBean | undefined {
    if(this.taskCategoryId){
      return FwDatas.get(this.taskCategoryId);
    }
  },
  quantityActive(this: TaskBean): boolean {
    return isFeatureEnabled('FWALL-33-list-item-quantity') && TaskListCache.get(this.taskListId).taskListType==="SHOPPING_LIST";
  },
  /** Empty when there is no quantity to show, 0 included: the badge is then not rendered at all. */
  quantityLabel(this: TaskBean): string {
    return this.quantity>0?formatQuantityUnit(this.quantity,this.unit):'';
  },
  subLine:function(this: TaskBean): string {
    let items: string[] = [];
    if(this.dueDate){
      items.push(Icons.calendar.ic_calendar.asIcon()+' '+deltatime.html(this.computedDueDate||this.dueDate,'deltaday'));
      if(this.recurrencyDescriptor.rrule){
        items[items.length-1] += ' '+Icons.task.ic_list_item_attribute_tiny_repeat.asIcon()
      }
    }
    if(this.refId && this.refObject()){
      items.push(Icons.task.ic_list_item_attribute_tiny_recipe.asIcon()+' '+this.refObject().name);
    }
    if(this.assignee.length){
      let str = Icons.ic_member_select.asIcon()+' ';
      if(this.assignee.length>4){
        str += this.assignee.slice(0,3).map(ass=>Html.escape(ass.name)).join(', ') + "& "+i18n.get("task_details.Nothers",(this.assignee.length-3));
      }else{
        str += this.assignee.map(ass=>Html.escape(ass.name)).join(', ');
      }
      items.push(str);
    }
    if(this.description){
      let desc = Icons.task.ic_list_item_attribute_note.asIcon()+' ';
      desc += '<span style="vertical-align:middle">'+Html.escape((this.description).replace(/\s+/g,' ').trim())+"</span>";//i18n.get('mp.description');// '<i style="max-width:30%;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">'+Html.escape(this.description)+'</i>';
      items.push(desc);
    }
    if(this.medias?.length){
      items.push(Icons.ic_attachment.asIcon()+" "+this.medias.map(m=>Html.escape(m.name)).join(', '))
    }
    return items.join(' • ')
  },
  refObject(this: TaskBean): RecipeBean | undefined {
    return FwDatas.get(this.refId)
  }
});




const TaskCache = new UndoableCache(new SyncCache<TaskBean>(ApiServer.tasksync,(a,b)=>{
  return b.sortingIndex-a.sortingIndex;
}));
const TaskCategoryCache = new SyncCache<TaskCategoryBean>(ApiServer.taskcategorysync);
const TaskListCache = new ListCache<ITaskList>(ApiServer.taskgettasklists);

const MetaTaskCache = new MetaCache4<
  ITaskList[],
  TaskCategoryBean[],
  TaskBean[],
  RecipeBean[]
  >(function(lists,categoryList,tasks,recipes){
    registerKnownUnits(tasks);
    lists.forEach(list=>{
      list.dispatchTasks(tasks)
    })
  },TaskListCache,TaskCategoryCache,TaskCache,recipeSyncCache);

let unitsRegistered = false;
/**
 * Units already in use in the family — custom ones, or ones written by another client — are offered
 * as chips by the quantity picker, and have to be known to the ingredient parser: the quick add
 * writes the picked unit into its input and only reads back what the parser recognizes. Once is
 * enough, the very first load carries every existing item.
 */
function registerKnownUnits(tasks :TaskBean[]){
  if(unitsRegistered) return;
  unitsRegistered = true;
  new Set(tasks.map(task=>task.unit).filter(Boolean)).forEach(unit=>rememberUnit(unit));
}

//callback should take lists,listsById tasks as parameters
ApiServer.loadTaskAndList = async function(callback){
  //if(typeof callback==='string') console.log(new Error())
  //recipe are needed by some tasks (added in Shopping from recipe)
  var [lists,categoryList,tasks,recipes] = await MetaTaskCache.refresh();

  var listsById = {},tasksById = {};
  tasks.forEach(task=>tasksById[task.metaId] = task)
  lists.forEach(list=>listsById[list.metaId] = list)

  amplify.publish("taskRefreshed",lists,listsById,tasks,tasksById,categoryList);
  if(callback){
    callback(lists,listsById,tasks,tasksById,categoryList);
  }
}
amplify.subscribe("taskNeedRefresh",ApiServer.loadTaskAndList)

export {MetaTaskCache,TaskCache,TaskCategoryCache,TaskListCache}
